
import styles from './AgentMessage.module.css';

import { Message, MessageAttachment } from "../../types/Agent";

import Markdown from 'react-markdown';

// import rehypeMathjax from 'rehype-mathjax';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import { ReactComponent as RollbackAgentIcon } from '../../assets/images/rollback-agent-icon.svg';
import LoadingSpinner from '../LoadingSpinner';

// Avatars removed from inline layout to maximize content width.
import React from 'react';
import ToolCallCard from './ToolCallCard';
import BlueprintCard from './BlueprintCard';
import AgentProgressTicker from './AgentProgressTicker';
import ExecutionLogCard from './ExecutionLogCard';
import { 
  AgentBadMessageError, 
  AgentCharLimitExceededError, 
  AuthorizationError, 
  InternalServerError, 
  PayloadTooLargeError, 
  UnexpectedError 
} from '@back4app2/errors';

// Attachment Preview Thumbnail Component
const AttachmentThumbnail = ({ 
  attachment, 
  onClick 
}: { 
  attachment: MessageAttachment; 
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className="relative group inline-block cursor-pointer"
      title={attachment.fileName}
    >
      <div className="h-10 w-10 rounded-md border border-light-blue/20 overflow-hidden bg-black/20 flex items-center justify-center hover:border-light-blue/40 transition-colors">
        <img 
          src={attachment.presignedUrl} 
          alt={attachment.fileName}
          className="max-w-full max-h-full object-contain"
        />
      </div>
    </button>
  );
};

const customStyle: {
  [key: string]: React.CSSProperties
} = {
  ...oneDark,
  'pre[class*="language-"]': {
    ...oneDark['pre[class*="language-"]'],
    backgroundColor: '#111214',
    margin: 0
  },
  'code[class*="language-"]': {
    ...oneDark['code[class*="language-"]'],
    backgroundColor: '#111214',
    margin: 0
  },
};

// Helper function to parse tool calls from the entire message content
// function parseToolCallsFromContent(content: string) {
//   const toolCalls: Array<{name: string, parameters: Record<string, any>, result?: any, startIndex: number, endIndex: number}> = [];
  
//   // Find all tool-call blocks
//   const toolCallPattern = /```tool-call\s*\n([\s\S]*?)\n```/g;
//   let match;
  
//   while ((match = toolCallPattern.exec(content)) !== null) {
//     const [fullMatch, jsonStr] = match;
    
//     try {
//       const callData = JSON.parse(jsonStr.trim());
//       const name = callData.name || callData.tool || 'unknown';
//       const parameters = callData.parameters || callData.params || {};
      
//       // Find corresponding result after this tool call
//       const afterMatch = content.substring(match.index + fullMatch.length);
//       const resultPattern = /```tool-result\s*\n([\s\S]*?)\n```/;
//       const resultMatch = afterMatch.match(resultPattern);
      
//       let result = undefined;
//       let endIndex = match.index + fullMatch.length;
      
//       if (resultMatch) {
//         try {
//           result = JSON.parse(resultMatch[1].trim());
//         } catch {
//           result = resultMatch[1].trim();
//         }
//         // Update endIndex to include the result block
//         endIndex = match.index + fullMatch.length + resultMatch[0].length;
//       }
      
//       toolCalls.push({ 
//         name, 
//         parameters, 
//         result, 
//         startIndex: match.index, 
//         endIndex 
//       });
//     } catch (e) {
//       console.warn('Failed to parse tool call:', e);
//     }
//   }
  
//   return toolCalls;
// }

// Stop marker pattern used by agent_v2.py - markdown format: \n\n---\n*message*
const STOP_MARKER_REGEX = /\n\n---\n\*This message generation was stopped by user\.\*\s*$/;
const LEGACY_TOOL_LINE_REGEX = /(?:\n\n)?Using tool:\s*`[^`]+`[^\n]*\n?/g;
const LEGACY_DONE_REGEX = /\s*Done\.\s*/g;

function extractProgressBlocks(content: string) {
  /**
   * IMPORTANT:
   * Do not reuse a global RegExp instance across renders. A /g regex keeps `lastIndex`,
   * which can cause progress blocks to be skipped in subsequent calls and the UI to
   * fall back to generic messages even when progress events are present.
   */
  const progressBlockRegex = /`{3,}agent-progress\s*\n([\s\S]*?)\n`{3,}/g;

  const events: any[] = [];
  let match;
  while ((match = progressBlockRegex.exec(content)) !== null) {
    try {
      const evt = JSON.parse(match[1].trim());
      events.push(evt);
    } catch {
      // ignore parse errors
    }
  }
  const cleaned = content
    .replace(/`{3,}agent-progress\s*\n[\s\S]*?\n`{3,}/g, '')
    .replace(LEGACY_TOOL_LINE_REGEX, '')
    .replace(LEGACY_DONE_REGEX, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  const lastEvent = events.length ? events[events.length - 1] : null;
  return { cleaned, lastEvent, events };
}

const AgentMessage = React.memo(
  ({
    message,
    onAttachmentClick,
    onRollback,
    isRollbacking,
  }: {
    message: Message;
    onAttachmentClick?: (attachment: MessageAttachment) => void;
    onRollback?: () => Promise<boolean>;
    isRollbacking?: boolean;
}) => {
  // Layout: user messages have subtle bg + rounded corners, agent messages transparent
  
  // Check if message was stopped by user (agent_v2 format)
  const { contentWithoutStopMarker, wasStoppedByUser } = React.useMemo(() => {
    if (message.role !== 'assistant') {
      return { contentWithoutStopMarker: message.content, wasStoppedByUser: false };
    }
    const match = message.content.match(STOP_MARKER_REGEX);
    if (match) {
      return {
        contentWithoutStopMarker: message.content.replace(STOP_MARKER_REGEX, ''),
        wasStoppedByUser: true
      };
    }
    return { contentWithoutStopMarker: message.content, wasStoppedByUser: false };
  }, [message.content, message.role]);

  // Remove technical progress/tool chatter and extract the latest progress event.
  // Also collect all EXECUTION events for the persistent log card.
  const { displayContent, progressEvent, executionEvents } = React.useMemo(() => {
    if (message.role !== 'assistant') {
      return { displayContent: message.content, progressEvent: null as any, executionEvents: [] as any[] };
    }
    const { cleaned, lastEvent, events } = extractProgressBlocks(contentWithoutStopMarker);
    // Collect events that have file changes (for the log card after typing)
    const execEvents = events.filter(
      (e: any) => e.changed || e.changes
    );
    return { displayContent: cleaned, progressEvent: lastEvent, executionEvents: execEvents };
  }, [contentWithoutStopMarker, message.content, message.role]);
  
  // Create a stable hash for the message content to use as part of the key
  const contentHash = React.useMemo(() => {
    // Create a more stable hash based on the entire content
    let hash = 0;
    for (let i = 0; i < message.content.length; i++) {
      const char = message.content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }, [message.content]);

  // Parse tool calls and results to create a lookup map
  const toolCallResults = React.useMemo(() => {
    if (message.role !== 'assistant') return new Map();
    
    const results = new Map();
    const toolCallPattern = /```tool-call\s*\n([\s\S]*?)\n```/g;
    let match;
    
    while ((match = toolCallPattern.exec(message.content)) !== null) {
      try {
        const callData = JSON.parse(match[1].trim());
        const name = callData.name || callData.tool || 'unknown';
        const parameters = callData.parameters || callData.params || {};
        
        // Find corresponding result after this tool call
        const afterMatch = message.content.substring(match.index + match[0].length);
        const resultPattern = /```tool-result\s*\n([\s\S]*?)\n```/;
        const resultMatch = afterMatch.match(resultPattern);
        
        let result = undefined;
        if (resultMatch) {
          try {
            result = JSON.parse(resultMatch[1].trim());
          } catch {
            result = resultMatch[1].trim();
          }
        }
        
        // Create a unique key for this tool call
        const toolKey = `${name}-${JSON.stringify(parameters)}`;
        results.set(toolKey, result);
      } catch (e) {
        console.warn('Failed to parse tool call:', e);
      }
    }
    
    return results;
  }, [message.content, message.role]);

  const handledAgentErrorMessage = React.useMemo(() => {
    if (!message.error || !message.error.code) return null;
    if (message.error.code === InternalServerError.CODE) return null;
    if (message.error.code === UnexpectedError.CODE) return null;
  
    if (message.error.code === AgentCharLimitExceededError.CODE || 
      message.error.code === PayloadTooLargeError.CODE) {
      return 'Message is too long. Please shorten the message and try again.';
    } else if (message.error.code === AgentBadMessageError.CODE) {
      return 'Message is not valid. Please rewrite it and try again.';
    } else if (message.error.code === AuthorizationError.CODE) {
      return 'You are not authorized to requested this action.';
    } else if (message.error.code === InternalServerError.CODE) {
      return 'Something went wrong. If this issue persists, please contact us.';
    }
    return null;
  }, [message.error]);

  const rollbackTooltipText =
    message.rollbackTooltip ||
    (message.rollbackDisabled ? 'Rollback not supported' : 'Revert to this message');

  return (
    <div className={`notranslate text-sm mb-3 ${message.role === 'user' ? 'rounded-2xl bg-white/[0.03] px-4 py-4' : 'px-4 py-4'}`} translate="no">
        {message.role === 'user' ? (
          <div className="max-w-3xl mx-auto">
            {/* Attachment previews */}
            {message.attachments && message.attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {message.attachments.map((attachment) => (
                  <AttachmentThumbnail
                    key={attachment.id}
                    attachment={attachment}
                    onClick={() => onAttachmentClick?.(attachment)}
                  />
                ))}
              </div>
            )}
            <div className="prose !prose-invert whitespace-pre-wrap w-full break-words [word-break:break-word]">
              {message.content}
            </div>
            {message.showRollback && onRollback && (
              <div className="flex justify-end">
                <span className={`inline-flex relative ${styles.rollbackTooltipTrigger}`}>
                  <button
                    type="button"
                    onClick={() => {
                      if (!message.rollbackDisabled && !isRollbacking) {
                        onRollback();
                      }
                    }}
                    disabled={message.rollbackDisabled || isRollbacking}
                    className="flex h-8 items-center justify-center text-light-blue/50 transition hover:text-light-blue disabled:cursor-not-allowed disabled:hover:text-light-blue/50"
                    aria-label={rollbackTooltipText}
                  >
                    {isRollbacking ? (
                      <LoadingSpinner width="1rem" height="1rem" />
                    ) : (
                      <RollbackAgentIcon className="h-[18px] w-[18px] [&_path]:fill-current" />
                    )}
                  </button>
                  <span className={styles.rollbackTooltipBubble} role="tooltip">
                    {rollbackTooltipText}
                  </span>
                </span>
              </div>
            )}
          </div>
        ) : (
          <div className='max-w-3xl mx-auto flex flex-col w-full overflow-hidden'>
            {message.hasFailed !== true ? <Markdown
              className="prose !prose-invert w-full break-words [word-break:break-word] overflow-hidden"
              remarkPlugins={[remarkGfm, remarkMath ]}
              // rehypePlugins={[rehypeMathjax]}
              components={{
                pre(props) {
                  const {children, ...rest} = props;
                  const codeElement = React.Children.only(children) as React.ReactElement<{className: string, children: React.ReactNode}>;
                  const {className, children: codeChildren} = codeElement.props;
                  const match = /language-([\w-]+)/.exec(className || '');
                  const codeContent = String(codeChildren).replace(/\n$/, '');
                  
                  // Check if this is a tool-call or tool-result code block
                  if (match && (match[1] === 'tool-call' || match[1] === 'tool-result')) {
                    // Hide technical tool blocks from user-facing chat.
                    return null;
                  }
                  
                  // Check for Blueprint JSON - don't wrap in <pre>, let code handler render the card
                  if (match && match[1] === 'json' && codeContent.includes('blueprint_title')) {
                    return <>{children}</>;
                  }
                  
                  return <pre {...rest}>{children}</pre>;
                },
                code(props) {
                  const {children, className, node, ...rest} = props
                  const match = /language-([\w-]+)/.exec(className || '');
                  const content = String(children).replace(/\n$/, '');
                  
                  // Check for Blueprint JSON (task_plan) - detect by blueprint_title key
                  if (match && match[1] === 'json' && content.includes('blueprint_title')) {
                    // Clean up potential XML tags if the model hallucinates them inside the code block
                    const jsonString = content.replace(/<\/?task_plan>/g, '').trim();
                    
                    // If still typing (streaming), show placeholder card
                    if (message.isTyping) {
                      return <BlueprintCard data={{ blueprint_title: 'Generating...' }} isTyping={true} />;
                    }
                    
                    // Try to parse the complete JSON
                    try {
                      const data = JSON.parse(jsonString);
                      if (data.blueprint_title) {
                        return <BlueprintCard data={data} isTyping={false} />;
                      }
                    } catch (e) {
                      // JSON not yet complete or invalid, show placeholder
                      return <BlueprintCard data={{ blueprint_title: 'Processing...' }} isTyping={true} />;
                    }
                  }
                  
                  return match && match[1] ? (
                    <div className='codeblock overflow-x-auto'>
                      <SyntaxHighlighter
                        children={content}
                        style={customStyle}
                        language={match[1]}
                        wrapLongLines={true}
                      />
                    </div>
                  ) : (
                    <code {...rest} className={`${className || ''} break-all`}>
                      {children}
                    </code>
                  )
                },
                table({ children }) {
                  return (
                    <table className="border-collapse border border-black px-3 py-1 dark:border-white">
                      {children}
                    </table>
                  );
                },
                th({ children }) {
                  return (
                    <th className="break-words border border-black bg-gray-500 px-3 py-1 text-white dark:border-white">
                      {children}
                    </th>
                  );
                },
                td({ children }) {
                  return (
                    <td className="break-words border border-black px-3 py-1 dark:border-white">
                      {children}
                    </td>
                  );
                },
                a({ href, className, children }) {
                  return (
                    <a className={className} href={href} target="_blank" rel="noreferrer noopener">
                      {children}
                    </a>
                  );
                }
              }}
            >
              {displayContent}
            </Markdown> : null}
            
            {/* Show stopped by user indicator */}
            {wasStoppedByUser && (
              <div className="text-[#9ca3af] text-[14px] italic mt-4">
                This message generation was stopped by user.
              </div>
            )}
            
            { handledAgentErrorMessage ? (
              <div className="mt-4 text-error-red">
                {handledAgentErrorMessage}
              </div>
            ) : message.hasFailed ? (
              <div className="mt-4 text-error-red">
                Something went wrong. If this issue persists, please contact us.
              </div>
            ) : (
              <>
                {/* Show progress while typing (non-technical) */}
                {message.isTyping && (
                  <AgentProgressTicker event={progressEvent} />
                )}

                {/* Persistent execution log (visible after typing finishes) */}
                {!message.isTyping && executionEvents.length > 0 && (
                  <ExecutionLogCard events={executionEvents} />
                )}
              </>
            )}
          </div>
        )}
    </div>
  )
}, (prevProps, nextProps) => {
  // Only re-render if the message content actually changes
  const prevAttachments = prevProps.message.attachments || [];
  const nextAttachments = nextProps.message.attachments || [];
  const attachmentsEqual = 
    prevAttachments.length === nextAttachments.length &&
    prevAttachments.every((att, i) => 
      att.id === nextAttachments[i]?.id &&
      att.presignedUrl === nextAttachments[i]?.presignedUrl
    );
  
  return prevProps.message.content === nextProps.message.content &&
        prevProps.message.role === nextProps.message.role &&
        prevProps.message.isTyping === nextProps.message.isTyping &&
        prevProps.message.hasFailed === nextProps.message.hasFailed &&
        prevProps.message.showRollback === nextProps.message.showRollback &&
        prevProps.message.rollbackDisabled === nextProps.message.rollbackDisabled &&
        prevProps.message.rollbackTooltip === nextProps.message.rollbackTooltip &&
        prevProps.isRollbacking === nextProps.isRollbacking &&
        attachmentsEqual;
});

export default AgentMessage;
